import makeStyles from '@mui/styles/makeStyles';
import React, { useMemo } from 'react';
import { useAppSelector } from '../../app/hooks';
import useIntervention from '../../hooks/intervention/useIntervention.hooks';
import { selectIsWebSocketConnected } from '../../selectors/message';
import Colors from '../../utils/color';
import { AutocompleteComp } from '../OmniBar/AutocompleteComp';

// HITL 3.0 Quick Response input. Same behavior as the OLD OmniBar, restyled to
// a compact 34px input so it lines up with the intent buttons in the bottom bar.
const useStyles = makeStyles({
  container: {
    width: '100%',
  },
  wrapForm: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    // The bordered box + fixed height live on the MUI input container itself
    // (.MuiInputBase-root), with the text flex-centered inside — so the input
    // is exactly the same 34px height as the intent buttons. Higher-specificity
    // nested selectors win against MUI's own emotion styles.
    '& .MuiAutocomplete-root': { width: '100%' },
    '& .MuiFormControl-root': { margin: 0, padding: 0 },
    '& .MuiInputBase-root': {
      height: 34,
      minHeight: 34,
      boxSizing: 'border-box',
      // !important overrides MUI Autocomplete's built-in 9px inputRoot padding
      // (equal specificity, but MUI's emotion styles win the injection-order
      // tie). That stray vertical padding is what pushed the text below center;
      // with it gone, the flex `alignItems: center` truly centers the input.
      padding: '0 10px !important',
      display: 'flex',
      alignItems: 'center',
      border: `1px solid ${Colors.alto}`,
      borderRadius: 8,
      backgroundColor: Colors.white,
    },
    '& .MuiInputBase-input': {
      padding: '0 !important',
      margin: 0,
      height: 'auto',
      fontSize: '0.8rem',
      // With the stray padding gone, pin the line box to the inner height (34px
      // − 2px borders) so the single line of text/placeholder is centered.
      lineHeight: '32px',
    },
  },
  // Staff intervention in progress → the whole input border turns red. The OLD
  // OmniBar showed a full red frame via a red container background behind a
  // padded form; the compact input has no padding for a background to show
  // through (it would only peek at the rounded corners), so the red lives on
  // the input border itself for a complete outline.
  interventionForm: {
    '& .MuiInputBase-root': {
      border: `2px solid ${Colors.torchRed}`,
    },
  },
});

export const OmniBarCompact = () => {
  const classes = useStyles();
  const isWebSocketConnected = useAppSelector(selectIsWebSocketConnected);
  const { isStaffInterventionHappening } = useIntervention();

  const isDisabledMode = useMemo(() => {
    if (isWebSocketConnected) {
      return !!isStaffInterventionHappening;
    }
    return true;
  }, [isWebSocketConnected, isStaffInterventionHappening]);

  return (
    <div data-testid="omnibar" className={classes.container}>
      <form
        className={`${classes.wrapForm}${
          isStaffInterventionHappening ? ` ${classes.interventionForm}` : ''
        }`}
        noValidate
        autoComplete="off"
      >
        <AutocompleteComp disabled={isDisabledMode} />
      </form>
    </div>
  );
};
